import { useDocumentTitle } from "@/lib/use-document-title";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { supabase } from "@/integrations/supabase/client";
import { downloadCsv } from "@/lib/csv";
import { Download, TrendingUp, FileText, History, Activity, Banknote } from "lucide-react";

// ---------- Data ----------
type TrendPoint = { month: string; new_members: number; cancelled: number; net: number; cumulative_active: number; mrr: number };

async function fetchTrends(): Promise<TrendPoint[]> {
  const [{ data: members }, { data: cancels }] = await Promise.all([
    supabase.from("members").select("created_at, cancelled_at, status"),
    supabase.from("members").select("cancelled_at").not("cancelled_at", "is", null),
  ]);
  const now = new Date();
  const months: TrendPoint[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = d.toISOString().slice(0, 7);
    months.push({ month: key, new_members: 0, cancelled: 0, net: 0, cumulative_active: 0, mrr: 0 });
  }
  const idx = new Map(months.map((m, i) => [m.month, i]));
  (members ?? []).forEach((m) => {
    const k = (m.created_at ?? "").slice(0, 7);
    if (idx.has(k)) months[idx.get(k)!].new_members++;
  });
  (cancels ?? []).forEach((c) => {
    const k = (c.cancelled_at ?? "").slice(0, 7);
    if (idx.has(k)) months[idx.get(k)!].cancelled++;
  });

  // Estimate cumulative active by counting all members that existed and weren't cancelled before each month end
  const all = members ?? [];
  months.forEach((m, i) => {
    const end = new Date(m.month + "-01");
    end.setMonth(end.getMonth() + 1);
    const active = all.filter((a) => {
      const created = a.created_at ? new Date(a.created_at) : null;
      if (!created || created >= end) return false;
      if (a.cancelled_at && new Date(a.cancelled_at) < end) return false;
      return true;
    }).length;
    m.cumulative_active = active;
    m.mrr = Math.round(active * 75.91);
    m.net = m.new_members - m.cancelled;
  });
  return months;
}

async function fetchAudit(limit = 100) {
  const { data, error } = await supabase
    .from("audit_log")
    .select("id, occurred_at, actor_email, table_name, record_id, action, changed_fields")
    .order("occurred_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

// ---------- Components ----------
function LineChart({ data, dataKey, color, label, fmt }: { data: TrendPoint[]; dataKey: keyof TrendPoint; color: string; label: string; fmt?: (n: number) => string }) {
  const W = 600, H = 180, P = 28;
  const values = data.map((d) => Number(d[dataKey]) || 0);
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const range = max - min || 1;
  const stepX = (W - P * 2) / Math.max(1, data.length - 1);
  const pts = values.map((v, i) => {
    const x = P + i * stepX;
    const y = H - P - ((v - min) / range) * (H - P * 2);
    return [x, y] as const;
  });
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${path} L${pts[pts.length - 1][0].toFixed(1)},${H - P} L${pts[0][0].toFixed(1)},${H - P} Z`;
  const f = fmt ?? ((n: number) => String(n));
  return (
    <div className="card-soft p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display font-semibold">{label}</h3>
        <span className="text-sm text-muted-foreground">Nu: <span className="font-bold text-foreground">{f(values[values.length - 1])}</span></span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
        <defs>
          <linearGradient id={`g-${String(dataKey)}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((t) => (
          <line key={t} x1={P} x2={W - P} y1={P + (H - P * 2) * t} y2={P + (H - P * 2) * t} stroke="currentColor" strokeOpacity="0.08" />
        ))}
        <path d={area} fill={`url(#g-${String(dataKey)})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3" fill={color} />
            <title>{`${data[i].month}: ${f(values[i])}`}</title>
          </g>
        ))}
        {data.map((d, i) => i % 2 === 0 ? (
          <text key={i} x={P + i * stepX} y={H - 6} textAnchor="middle" className="fill-current text-[10px] opacity-50">{d.month.slice(5)}</text>
        ) : null)}
      </svg>
    </div>
  );
}

function ExportCard({ title, hint, onExport }: { title: string; hint: string; onExport: () => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      onClick={async () => { setBusy(true); try { await onExport(); } finally { setBusy(false); } }}
      disabled={busy}
      className="card-soft p-5 text-left hover:border-primary transition disabled:opacity-50 w-full"
    >
      <div className="flex items-center justify-between mb-2">
        <FileText size={18} className="text-primary" />
        <Download size={14} className="text-muted-foreground" />
      </div>
      <div className="font-display font-semibold">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{busy ? "Förbereder…" : hint}</div>
    </button>
  );
}

function fmtSek(n: number) { return new Intl.NumberFormat("sv-SE").format(n) + " kr"; }

const actionTone: Record<string, string> = {
  INSERT: "bg-emerald-500/15 text-emerald-700",
  UPDATE: "bg-amber-500/15 text-amber-700",
  DELETE: "bg-rose-500/15 text-rose-700",
};

function ReportsPage() {
  useDocumentTitle("Rapporter — ÅF-Portal");
  const { data: trends = [], isLoading } = useQuery({ queryKey: ["report-trends"], queryFn: fetchTrends });
  const { data: audit = [] } = useQuery({ queryKey: ["audit-log"], queryFn: () => fetchAudit(100) });

  const [auditFilter, setAuditFilter] = useState<string>("all");
  const filteredAudit = useMemo(
    () => auditFilter === "all" ? audit : audit.filter((a) => a.table_name === auditFilter),
    [audit, auditFilter]
  );
  const tables = useMemo(() => Array.from(new Set(audit.map((a) => a.table_name))).sort(), [audit]);

  const exportTable = async (table: string, filename: string) => {
    const { data, error } = await supabase.from(table as any).select("*").limit(10000);
    if (error) { alert(`Kunde inte hämta: ${error.message}`); return; }
    if (!data?.length) { alert("Inga rader att exportera."); return; }
    downloadCsv(filename, data as any);
  };

  return (
    <StaffLayout title="Rapporter & revision">
      <p className="text-muted-foreground mb-8 max-w-2xl">Trender över 12 månader, dataexporter för bokföring/analys och fullständig revisionslogg.</p>

      {/* Trends */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-primary" />
          <h2 className="!text-lg !mb-0">Trender — 12 månader</h2>
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Laddar trender…</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            <LineChart data={trends} dataKey="cumulative_active" color="hsl(var(--primary))" label="Aktiva medlemmar" />
            <LineChart data={trends} dataKey="mrr" color="hsl(142 71% 45%)" label="MRR" fmt={fmtSek} />
            <LineChart data={trends} dataKey="new_members" color="hsl(217 91% 60%)" label="Nya medlemmar / mån" />
            <LineChart data={trends} dataKey="cancelled" color="hsl(0 84% 60%)" label="Uppsägningar / mån" />
          </div>
        )}
      </section>

      {/* Bookkeeping monthly export */}
      <BookkeepingSection />

      {/* Generic CSV exports */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Download size={18} className="text-primary" />
          <h2 className="!text-lg !mb-0">CSV-export</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <ExportCard title="Medlemmar" hint="Hela medlemsregistret" onExport={() => exportTable("members", `medlemmar-${today()}.csv`)} />
          <ExportCard title="Fakturor" hint="Alla fakturor & betalningar" onExport={() => exportTable("invoices", `fakturor-${today()}.csv`)} />
          <ExportCard title="Förmånsanvändning" hint="Aktiverade förmåner" onExport={() => exportTable("benefit_logs", `formaner-${today()}.csv`)} />
          <ExportCard title="Partneranvändning" hint="Partner-rabatter använda" onExport={() => exportTable("partner_usage_logs", `partners-${today()}.csv`)} />
          <ExportCard title="Värvningar" hint="Vän värvar vän-flödet" onExport={() => exportTable("referrals", `varvningar-${today()}.csv`)} />
          <ExportCard title="Eventanmälningar" hint="Bokade event" onExport={() => exportTable("event_registrations", `event-${today()}.csv`)} />
        </div>
      </section>

      {/* Audit log */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <History size={18} className="text-primary" />
            <h2 className="!text-lg !mb-0">Revisionslogg</h2>
            <span className="text-xs text-muted-foreground">(senaste 100 händelserna)</span>
          </div>
          <div className="flex items-center gap-2">
            <select value={auditFilter} onChange={(e) => setAuditFilter(e.target.value)} className="input-mini !py-1 !text-xs">
              <option value="all">Alla tabeller</option>
              {tables.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button
              onClick={() => downloadCsv(`revisionslogg-${today()}.csv`, filteredAudit as any)}
              className="btn-pill !text-xs !py-1.5"
            >
              <Download size={12} /> Exportera
            </button>
          </div>
        </div>
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Tid</th>
                <th className="px-4 py-3">Användare</th>
                <th className="px-4 py-3">Tabell</th>
                <th className="px-4 py-3">Åtgärd</th>
                <th className="px-4 py-3">Post</th>
                <th className="px-4 py-3">Ändrade fält</th>
              </tr>
            </thead>
            <tbody>
              {filteredAudit.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground whitespace-nowrap">{new Date(a.occurred_at).toLocaleString("sv-SE")}</td>
                  <td className="px-4 py-2.5 text-xs">{a.actor_email ?? <span className="text-muted-foreground">system</span>}</td>
                  <td className="px-4 py-2.5 text-xs font-mono">{a.table_name}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-display font-semibold ${actionTone[a.action] ?? ""}`}>{a.action}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground truncate max-w-[140px]">{a.record_id?.slice(0, 8)}…</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{a.changed_fields?.length ? a.changed_fields.slice(0, 4).join(", ") + (a.changed_fields.length > 4 ? "…" : "") : "—"}</td>
                </tr>
              ))}
              {!filteredAudit.length && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground"><Activity size={18} className="inline mr-2" />Inga händelser ännu.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </StaffLayout>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }

function currentMonth() { return new Date().toISOString().slice(0, 7); }

function BookkeepingSection() {
  const [month, setMonth] = useState<string>(currentMonth());
  const [busy, setBusy] = useState(false);

  const { data: summary } = useQuery({
    queryKey: ["bookkeeping-summary", month],
    queryFn: async () => {
      const start = `${month}-01T00:00:00Z`;
      const end = new Date(new Date(start).getFullYear(), new Date(start).getMonth() + 1, 1).toISOString();
      const { data, error } = await supabase
        .from("invoices" as any)
        .select("amount_sek, status, paid_at, period")
        .gte("paid_at", start)
        .lt("paid_at", end)
        .eq("status", "paid");
      if (error) throw error;
      const rows = (data ?? []) as any[];
      const total = rows.reduce((s, r) => s + (r.amount_sek || 0), 0);
      return { count: rows.length, total };
    },
  });

  async function exportMonth() {
    setBusy(true);
    try {
      const start = `${month}-01T00:00:00Z`;
      const end = new Date(new Date(start).getFullYear(), new Date(start).getMonth() + 1, 1).toISOString();
      const { data, error } = await supabase
        .from("invoices" as any)
        .select("id, member_id, period, amount_sek, status, paid_at, created_at")
        .gte("paid_at", start)
        .lt("paid_at", end)
        .order("paid_at", { ascending: true });
      if (error) { alert(`Fel: ${error.message}`); return; }
      if (!data?.length) { alert("Inga fakturor för perioden."); return; }
      const rows = (data as any[]).map((r) => ({
        fakturanr: r.id,
        medlems_id: r.member_id,
        period: r.period ?? "",
        belopp_sek: r.amount_sek,
        moms_sek: Math.round((r.amount_sek || 0) * 0.2),
        netto_sek: Math.round((r.amount_sek || 0) * 0.8),
        status: r.status,
        betald_datum: r.paid_at ? new Date(r.paid_at).toISOString().slice(0, 10) : "",
        skapad: r.created_at ? new Date(r.created_at).toISOString().slice(0, 10) : "",
      }));
      downloadCsv(`bokforing-${month}.csv`, rows);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <Banknote size={18} className="text-primary" />
        <h2 className="!text-lg !mb-0">Bokföringsexport</h2>
      </div>
      <div className="card-soft p-5 flex flex-col sm:flex-row sm:items-end gap-4 max-w-2xl">
        <div className="flex-1">
          <label className="label-field">Period (månad)</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="input-field"
          />
          {summary && (
            <p className="text-xs text-muted-foreground mt-2">
              {summary.count} betalda fakturor — totalt <strong className="text-foreground">{fmtSek(summary.total)}</strong> (varav moms ca {fmtSek(Math.round(summary.total * 0.2))})
            </p>
          )}
        </div>
        <button onClick={exportMonth} disabled={busy} className="btn-pill !text-sm disabled:opacity-50">
          <Download size={14} /> {busy ? "Förbereder…" : "Exportera CSV"}
        </button>
      </div>
    </section>
  );
}

export default ReportsPage;
